import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppwriteApiEndpoint, AppwriteApiRoute, backupDataserverApiPort, backupDataserverDomainName, ClientDevice, DataserverApiEndpoint, EnvironmentId, onsiteUtilitiesApiPort, DataserverApiRoute, onsiteUtilitiesDomainName, GetLoginTokenForClientDeviceResponse, GetLoginTokenFromBadgeIdResponse, MotionmasterControlState, MotionmasterDirectoryResponse, MotionmasterProgramResponse, MotionmasterSelectProgramRequest, MotionmasterStartProgramRequest, MotionmasterStopProgramRequest, MotionmasterStatusResponse, MotionmasterToolpath } from '@prism/prism-shared';
import { environment } from 'apps/prism-client/src/environments/environment';
import { catchError, map, Observable, of, switchMap, tap, timeout } from 'rxjs';
import { AppwriteFunctionsService } from './appwrite-functions.service';
import { AppwriteAuthService } from './appwrite-auth.service';



enum HttpMethod {
    POST = 'POST',
    GET = 'GET',
}

@Injectable({ providedIn: 'root' })
export class DataserverApiService {

    //if hostname to ip mapping is set up, setting to true allows for printing without running dataserver. only for testing locally
    private useRemoteDataserver = true;

    private fallbackDataserverRequestWrapper<R>(urlSuffix: string, method: HttpMethod.GET, body?: null): Observable<R>;
    private fallbackDataserverRequestWrapper<B, R>(urlSuffix: string, method: HttpMethod.POST, body: B): Observable<R>;
    private fallbackDataserverRequestWrapper<B, R>(
        // exclude base URL
        urlSuffix: string,
        method: HttpMethod,
        body: B | null = null,
    ) {

        const primaryUrl = this.useRemoteDataserver
            ? `https://${onsiteUtilitiesDomainName}:${onsiteUtilitiesApiPort}/${urlSuffix}`
            : `${environment.urls.baseDataserverUrl}/${urlSuffix}`;

        const backupUrl = this.useRemoteDataserver
            ? `https://${backupDataserverDomainName}:${backupDataserverApiPort}/${urlSuffix}`
            : `${environment.urls.baseBackupDataserverUrl}/${urlSuffix}`;

        if (method === HttpMethod.GET) {
            return this.http.get<R>(primaryUrl).pipe(
                catchError(() => {
                    return this.http.get<R>(backupUrl)
                })
            )
        }
        if (method === HttpMethod.POST) {
            return this.http.post<R>(primaryUrl, body).pipe(
                catchError(() => {
                    return this.http.post<R>(backupUrl, body)
                })
            )
        }
        return of(null as R);
    }

    constructor(
        private http: HttpClient,
        private functions: AppwriteFunctionsService,
        private auth: AppwriteAuthService,
    ) { }

    // How long to wait on the onsite-utilities reachability probe before deciding we're
    // off-network and routing the print through the api proxy instead. Off-network there is
    // no fast failure — the TCP connection to an unreachable host just hangs (the OS connect
    // timeout is 20s+), so this timeout IS the detection cost. On-network the full
    // primary→backup probe answers in ~100ms, so 500ms leaves ample headroom while halving
    // the off-network wait. Lower it further to trade backup-probe headroom for more speed.
    private static readonly PING_TIMEOUT_MS = 500;


    // --------------------------------------------------
    // auth
    // --------------------------------------------------
    public getLoginTokenFromBadgeId(userBadgeId: string): Observable<GetLoginTokenFromBadgeIdResponse> {
        return this.fallbackDataserverRequestWrapper(
            `${DataserverApiRoute.Auth}/${DataserverApiEndpoint.GetLoginTokenFromBadgeId}`,
            HttpMethod.POST,
            { userBadgeId }
        )
    }

    public getLoginTokenForDashboard(clientDevice: ClientDevice): Observable<GetLoginTokenFromBadgeIdResponse> {
        return this.fallbackDataserverRequestWrapper(
            `${DataserverApiRoute.Auth}/${DataserverApiEndpoint.GetLoginTokenForDashboard}`,
            HttpMethod.POST,
            { clientDevice },
        )
    }

    public getLoginTokenForClientDevice(clientDevice: ClientDevice): Observable<GetLoginTokenForClientDeviceResponse> {
        return this.fallbackDataserverRequestWrapper(
            `${DataserverApiRoute.Auth}/${DataserverApiEndpoint.GetLoginTokenForClientDevice}`,
            HttpMethod.POST,
            { clientDevice }
        )
    }


    // --------------------------------------------------
    // utility
    // --------------------------------------------------

    public ping(): Observable<boolean> {
        return this.fallbackDataserverRequestWrapper<boolean>(
            `${DataserverApiRoute.Utility}/${DataserverApiEndpoint.Ping}`,
            HttpMethod.GET,
        )
    }


    public checkIfIAmClientDevice(): Observable<ClientDevice | null> {
        return this.fallbackDataserverRequestWrapper<ClientDevice | null>(
            `${DataserverApiRoute.Utility}/${DataserverApiEndpoint.CheckIfIAmClientDevice}`,
            HttpMethod.GET,
        ).pipe(
            catchError((e) => {
                console.error("checkIfIAmClientDevice error: ", e)
                return of(null)
            })
        )
    }

    public getDeviceIpAddress(): Observable<string> {
        return this.fallbackDataserverRequestWrapper<string>(
            `${DataserverApiRoute.Utility}/${DataserverApiEndpoint.GetDeviceIpAddress}`,
            HttpMethod.GET,
        ).pipe(
            tap((ipAddress) => {
                console.log("getDeviceIpAddress ipAddress: ", ipAddress)
                return ipAddress || "resolution failed"
            }),
        )
    }


    // --------------------------------------------------
    // printing
    // --------------------------------------------------

    /**
     * Sends a ZPL string to a specified label printer for printing.
     * When printing a label, call this function twice. One label for internal tracking, one for shipping.
     *
     * Routes the print to exactly one path, decided up front by a fast onsite-utilities
     * reachability probe: on-network goes direct to onsite-utilities (instant); off-network
     * falls back to the prism-server `api` function, which forwards to onsite-utilities. We
     * pick the path *before* sending — never "try direct, then retry via proxy" — because the
     * ZPL can carry a `^PQ` copy count, so a retry of a delayed-but-successful print would
     * reprint the whole batch (e.g. 20 → 40 labels).
     *
     * @param {string} labelPrinterId - The unique identifier of the label printer to use.
     * @param {string} zpl - The ZPL string containing the label design and data to be printed.
     * @returns {Observable<boolean>} An observable that emits `true` if the printing request was successfully sent, and `false` if an error occurrs
     */
    public printLabel(
        labelPrinterId: string,
        zpl: string,
    ): Observable<boolean> {
        const body = { labelPrinterId, zpl };
        return this.canReachOnsiteUtilities().pipe(
            switchMap((reachable) => reachable ? this.directPrint(body) : this.proxyPrint(body)),
        );
    }

    // Fast probe: can we reach onsite-utilities directly? Goes through the same
    // primary→backup wrapper as real requests, so once a backup dataserver exists this
    // routing honors it (primary down + backup up → still prints direct, not via proxy).
    // A timeout or error (e.g. off-network) means no — route through the proxy instead.
    private canReachOnsiteUtilities(): Observable<boolean> {
        return this.ping().pipe(
            timeout(DataserverApiService.PING_TIMEOUT_MS),
            map(() => true),
            catchError(() => of(false)),
        );
    }

    // On-network path: straight to onsite-utilities (primary, then backup dataserver).
    private directPrint(body: { labelPrinterId: string; zpl: string; }): Observable<boolean> {
        return this.fallbackDataserverRequestWrapper<{ labelPrinterId: string; zpl: string; }, unknown>(
            `${DataserverApiRoute.Printing}/${DataserverApiEndpoint.PrintLabel}`,
            HttpMethod.POST,
            body,
        ).pipe(
            map((res) => this.isPrintSuccess(res)),
            catchError((e) => {
                console.error("label print error: ", e)
                return of(false)
            }),
        );
    }

    // Off-network path: forward through the prism-server `api` function, which reaches
    // onsite-utilities from inside the network. isLocal hits the local prism-server directly.
    private proxyPrint(body: { labelPrinterId: string; zpl: string; }): Observable<boolean> {
        const isLocal = environment.environmentId === EnvironmentId.LocalTesting
            || environment.environmentId === EnvironmentId.LocalStaging
            || environment.environmentId === EnvironmentId.LocalProduction;
        const path = `/${AppwriteApiRoute.Utility}/${AppwriteApiEndpoint.PrintLabel}`;
        const source = isLocal
            ? this.http.post<unknown>(`http://localhost:3333${path}`, body)
            : this.functions.post<unknown>('api', path, body);
        return source.pipe(
            map((res) => this.isPrintSuccess(res)),
            catchError((e) => {
                console.error("label proxy print error: ", e)
                return of(false)
            }),
        );
    }

    // --------------------------------------------------
    // motionmaster (Fagor 8050 M router)
    //
    // Shop-LAN only. Every call goes to the Mac mini wired to the control, through onsite-utilities
    // (apps/onsite-utilities/src/api/motionmaster.api.ts). There is deliberately no prism-server
    // proxy path: these either occupy the serial link or change what the control is doing.

    /**
     * Link status plus the control's live state. Emits null when onsite-utilities can't be reached from
     * here (off-network), so the page can show "not reachable" without waiting on a hung connection.
     * Errors through as HttpErrorResponse when the bridge is reached but the mini or control is not.
     */
    public getMotionmasterStatus(): Observable<MotionmasterStatusResponse | null> {
        return this.canReachOnsiteUtilities().pipe(
            switchMap((reachable) => reachable
                ? this.fallbackDataserverRequestWrapper<MotionmasterStatusResponse>(
                    `${DataserverApiRoute.Motionmaster}/${DataserverApiEndpoint.MotionmasterStatus}`,
                    HttpMethod.GET,
                )
                : of(null)),
        );
    }

    /** The control's program directory. refresh=true walks it over the serial link (slow); otherwise the mini's cache. */
    public getMotionmasterDirectory(refresh = false): Observable<MotionmasterDirectoryResponse> {
        return this.fallbackDataserverRequestWrapper<MotionmasterDirectoryResponse>(
            `${DataserverApiRoute.Motionmaster}/${DataserverApiEndpoint.MotionmasterDirectory}${refresh ? '?refresh=1' : ''}`,
            HttpMethod.GET,
        );
    }

    /** The program as it is on the control right now ('control'), or the mini's last copy of it ('library'). */
    public getMotionmasterProgram(programNumber: number, source: 'control' | 'library' = 'control'): Observable<MotionmasterProgramResponse> {
        return this.fallbackDataserverRequestWrapper<MotionmasterProgramResponse>(
            `${DataserverApiRoute.Motionmaster}/${DataserverApiEndpoint.MotionmasterProgram}/${programNumber}${source === 'library' ? '?source=library' : ''}`,
            HttpMethod.GET,
        );
    }

    /** The mini's toolpath model of the program (drawn from its library copy, refreshed on every read). */
    public getMotionmasterToolpath(programNumber: number): Observable<MotionmasterToolpath> {
        return this.fallbackDataserverRequestWrapper<MotionmasterToolpath>(
            `${DataserverApiRoute.Motionmaster}/${DataserverApiEndpoint.MotionmasterProgram}/${programNumber}/${DataserverApiEndpoint.MotionmasterToolpath}`,
            HttpMethod.GET,
        );
    }

    /** Selects the program for execution on the control (MAIN MENU > EXECUTE > number). Does not start it. */
    public selectMotionmasterProgram(programNumber: number): Observable<MotionmasterControlState> {
        return this.fallbackDataserverRequestWrapper<MotionmasterSelectProgramRequest, MotionmasterControlState>(
            `${DataserverApiRoute.Motionmaster}/${DataserverApiEndpoint.MotionmasterSelectProgram}`,
            HttpMethod.POST,
            { programNumber },
        );
    }

    /** Cycle Start is never retried on the backup server: a lost reply may mean the machine already started. */
    public startMotionmasterProgram(programNumber: number, expectedDigest: string): Observable<MotionmasterControlState> {
        const request: MotionmasterStartProgramRequest = { programNumber, expectedDigest };
        return this.auth.createJwt().pipe(
            switchMap((jwt) => this.http.post<MotionmasterControlState>(
                `https://${onsiteUtilitiesDomainName}:${onsiteUtilitiesApiPort}/${DataserverApiRoute.Motionmaster}/${DataserverApiEndpoint.MotionmasterStartProgram}`,
                request,
                { headers: { Authorization: `Bearer ${jwt}` } },
            )),
        );
    }

    /** Cycle Stop is a single command; do not retry a lost reply without checking the machine. */
    public stopMotionmasterProgram(programNumber: number): Observable<MotionmasterControlState> {
        const request: MotionmasterStopProgramRequest = { programNumber };
        return this.auth.createJwt().pipe(
            switchMap((jwt) => this.http.post<MotionmasterControlState>(
                `https://${onsiteUtilitiesDomainName}:${onsiteUtilitiesApiPort}/${DataserverApiRoute.Motionmaster}/${DataserverApiEndpoint.MotionmasterStopProgram}`,
                request,
                { headers: { Authorization: `Bearer ${jwt}` } },
            )),
        );
    }

    // onsite-utilities (and the proxy) reply { success: true } on success; a missing printer
    // comes back as body `false` and socket errors as 5xx — all of which are not-success here.
    private isPrintSuccess(res: unknown): boolean {
        return !!res && typeof res === 'object' && (res as { success?: unknown }).success === true;
    }
}
